import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCreateAdminMutation } from "../api/adminsApi";

import { useGetDepartmentsQuery } from "../../department/api/departmentsApi";
import { useGetDesignationsQuery } from "../../designation/api/designationsApi";
import {
  useGetStatesQuery,
  useGetLocalGovernmentsQuery,
} from "../../application/api/probationerApplicationApi";

type Option = { id: number; name: string };

export default function AdminCreateWizardPage() {
  const navigate = useNavigate();

  const { data: departmentsRaw = [] } = useGetDepartmentsQuery();
  const { data: designationsRaw = [] } = useGetDesignationsQuery();

  const departments: Option[] = useMemo(
    () =>
      (departmentsRaw || []).map((d: any) => ({
        id: Number(d.id),
        name: d.name,
      })),
    [departmentsRaw]
  );

  const designations: Option[] = useMemo(
    () =>
      (designationsRaw || []).map((d: any) => ({
        id: Number(d.id),
        name: d.name,
      })),
    [designationsRaw]
  );

  const { data: statesRaw = [] } = useGetStatesQuery();
  const states = Array.isArray(statesRaw) ? statesRaw : [];

  const [stateOfOriginId, setStateOfOriginId] = useState<number | null>(null);
  const { data: lgasRaw = [] } = useGetLocalGovernmentsQuery(
    stateOfOriginId!,
    { skip: !stateOfOriginId },
  );
  const lgas = Array.isArray(lgasRaw) ? lgasRaw : [];

  const [contactStateId, setContactStateId] = useState<number | null>(null);
  const { data: contactLgasRaw = [] } = useGetLocalGovernmentsQuery(
    contactStateId!,
    { skip: !contactStateId },
  );
  const contactLgas = Array.isArray(contactLgasRaw) ? contactLgasRaw : [];

  const [createAdmin, { isLoading }] = useCreateAdminMutation();

  const [step, setStep] = useState(1);
  const [msg, setMsg] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const firstInvalidRef = useRef<HTMLInputElement | HTMLSelectElement | null>(
    null
  );

  // ✅ file state for pro_pic (multipart)
  const [proPic, setProPic] = useState<File | null>(null);

  const [form, setForm] = useState({
    // account
    email: "",
    whois: "admin" as const,

    // required org
    department_id: 0,
    designation_id: 0,

    // names
    firstname: "",
    othername: "",
    lastname: "",

    // extended (optional)
    staff_no: "",
    gender: "",
    dob: "",
    marital_status: "",
    nationality: "",
    state_of_origin: "",
    lga: "",

    phone: "",
    phone2: "",
    work_email: "",

    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    contact_lga: "",
    postal_code: "",
    country: "",

    employment_date: "",
    employment_type: "",
    job_title: "",

    emergency_name: "",
    emergency_relationship: "",
    emergency_phone: "",
    emergency_address: "",

    nok_name: "",
    nok_relationship: "",
    nok_phone: "",
    nok_email: "",

    bank_name: "",
    bank_account_name: "",
    bank_account_number: "",
    bank_sort_code: "",

    notes: "",
  });

  const set = (k: string, v: any) => setForm((s) => ({ ...s, [k]: v }));

  const focusInvalid = () =>
    setTimeout(() => firstInvalidRef.current?.focus(), 0);

  const validateStep = (s: number) => {
    firstInvalidRef.current = null;

    // Minimal required fields per backend store()
    if (s === 1) {
      const emailOk = !!form.email && form.email.includes("@");
      const deptOk = Number(form.department_id) > 0;
      const desOk = Number(form.designation_id) > 0;
      const fnOk = !!form.firstname;
      const lnOk = !!form.lastname;

      return emailOk && deptOk && desOk && fnOk && lnOk;
    }

    return true;
  };

  const next = () => {
    setMsg(null);
    if (!validateStep(step)) {
      setMsg({
        type: "error",
        text: "Please fill the required fields for this step.",
      });
      focusInvalid();
      return;
    }
    setStep((s) => Math.min(4, s + 1));
  };

  const back = () => setStep((s) => Math.max(1, s - 1));

  // ✅ build FormData to match Laravel validation & file upload
  const buildFormData = () => {
    const fd = new FormData();

    // required + optional fields
    fd.append("email", form.email);
    fd.append("whois", "admin");

    fd.append("department_id", String(Number(form.department_id)));
    fd.append("designation_id", String(Number(form.designation_id)));

    fd.append("firstname", form.firstname);
    if (form.othername) fd.append("othername", form.othername);
    fd.append("lastname", form.lastname);

    if (form.staff_no) fd.append("staff_no", form.staff_no);
    if (form.gender) fd.append("gender", form.gender);
    if (form.dob) fd.append("dob", form.dob);
    if (form.marital_status) fd.append("marital_status", form.marital_status);
    if (form.nationality) fd.append("nationality", form.nationality);
    if (form.state_of_origin)
      fd.append("state_of_origin", form.state_of_origin);
    if (form.lga) fd.append("lga", form.lga);

    if (form.phone) fd.append("phone", form.phone);
    if (form.phone2) fd.append("phone2", form.phone2);
    if (form.work_email) fd.append("work_email", form.work_email);

    if (form.address_line1) fd.append("address_line1", form.address_line1);
    if (form.address_line2) fd.append("address_line2", form.address_line2);
    if (form.city) fd.append("city", form.city);
    if (form.state) fd.append("state", form.state);
    if (form.contact_lga) fd.append("contact_lga", form.contact_lga);
    if (form.postal_code) fd.append("postal_code", form.postal_code);
    if (form.country) fd.append("country", form.country);

    if (form.employment_date)
      fd.append("employment_date", form.employment_date);
    if (form.employment_type)
      fd.append("employment_type", form.employment_type);
    if (form.job_title) fd.append("job_title", form.job_title);

    if (form.emergency_name) fd.append("emergency_name", form.emergency_name);
    if (form.emergency_relationship)
      fd.append("emergency_relationship", form.emergency_relationship);
    if (form.emergency_phone)
      fd.append("emergency_phone", form.emergency_phone);
    if (form.emergency_address)
      fd.append("emergency_address", form.emergency_address);

    if (form.nok_name) fd.append("nok_name", form.nok_name);
    if (form.nok_relationship)
      fd.append("nok_relationship", form.nok_relationship);
    if (form.nok_phone) fd.append("nok_phone", form.nok_phone);
    if (form.nok_email) fd.append("nok_email", form.nok_email);

    if (form.bank_name) fd.append("bank_name", form.bank_name);
    if (form.bank_account_name)
      fd.append("bank_account_name", form.bank_account_name);
    if (form.bank_account_number)
      fd.append("bank_account_number", form.bank_account_number);
    if (form.bank_sort_code) fd.append("bank_sort_code", form.bank_sort_code);

    if (form.notes) fd.append("notes", form.notes);

    // ✅ file
    if (proPic) fd.append("pro_pic", proPic);

    return fd;
  };

  const onSubmit = async () => {
    setMsg(null);

    // Validate step1 at least
    if (!validateStep(1)) {
      setMsg({ type: "error", text: "Please fill required fields (Step 1)." });
      setStep(1);
      focusInvalid();
      return;
    }

    try {
      const fd = buildFormData();

      // IMPORTANT: send FormData (multipart)
      const res = await createAdmin(fd as any).unwrap();

      if (res?.status && res.status !== "success") {
        throw { data: { message: res?.message || "Create failed" } };
      }

      const created = res?.data || res?.data?.data;
      if (!created?.id) {
        throw { data: { message: "Create response missing admin id" } };
      }

      setMsg({
        type: "success",
        text: res?.message || "Admin created successfully",
      });
      setTimeout(() => navigate(`/admins/${created.id}`), 2000);
    } catch (e: any) {
      // Abort errors mean the request was cancelled (e.g. component unmount).
      // The server may have processed it — send the user to the list to verify.
      if (
        e?.status === "FETCH_ERROR" &&
        // The abort is now reported under `originalError` — `error` carries the
        // member-facing network message (see apiSlice).
        `${e?.originalError ?? ""}${e?.error ?? ""}`.includes("AbortError")
      ) {
        setMsg({ type: "success", text: "Request sent — verifying…" });
        setTimeout(() => navigate("/admins"), 1500);
        return;
      }

      console.error("Admin create error:", e);

      // Laravel 422 validation errors → flatten all field messages
      const validationErrors: Record<string, string[]> | undefined =
        e?.data?.errors;
      if (validationErrors) {
        const flat = Object.entries(validationErrors)
          .map(([field, msgs]) => `${field}: ${(msgs as string[]).join(", ")}`)
          .join(" | ");
        setMsg({ type: "error", text: flat });
        return;
      }

      // Standard API error message
      const text =
        e?.data?.message ||
        e?.data?.error ||
        e?.error ||
        (typeof e?.data === "string" ? e.data : null) ||
        `Error ${e?.status ?? ""} — check console for details`;

      setMsg({ type: "error", text });
    }
  };

  useEffect(() => setMsg(null), [step]);

  return (
    <div className="bg-white border rounded-xl p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Create Admin</h1>
          <p className="text-sm text-gray-500 mt-1">
            Multi-step staff profile creation (Account → Personal → Contact →
            Employment)
          </p>
        </div>
        <div className="text-sm text-gray-600">
          Step <span className="font-semibold">{step}</span> / 4
        </div>
      </div>

      {msg && (
        <div
          className={`mt-4 rounded-lg border p-3 text-sm ${
            msg.type === "success"
              ? "bg-green-50 border-green-200 text-green-800"
              : "bg-red-50 border-red-200 text-red-800"
          }`}
        >
          {msg.text}
        </div>
      )}

      <div className="mt-6">
        {step === 1 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Email *">
              <input
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
                placeholder="admin@email.com"
                autoFocus
              />
            </Field>

            <Field label="Work Email (optional)">
              <input
                value={form.work_email}
                onChange={(e) => set("work_email", e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
                placeholder="work@email.com"
              />
            </Field>

            <Field label="Department *">
              <select
                value={form.department_id}
                onChange={(e) => set("department_id", e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value={0}>Select department</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Designation *">
              <select
                value={form.designation_id}
                onChange={(e) => set("designation_id", e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value={0}>Select designation</option>
                {designations.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="First name *">
              <input
                value={form.firstname}
                onChange={(e) => set("firstname", e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </Field>

            <Field label="Other name">
              <input
                value={form.othername}
                onChange={(e) => set("othername", e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </Field>

            <Field label="Last name *">
              <input
                value={form.lastname}
                onChange={(e) => set("lastname", e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </Field>

            <Field label="Staff No (optional)">
              <input
                value={form.staff_no}
                onChange={(e) => set("staff_no", e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
                placeholder="STAFF-0001"
              />
            </Field>

            {/* ✅ NEW: Profile photo upload (pro_pic) */}
            <div className="md:col-span-2 border rounded-xl p-4">
              <div className="font-semibold text-gray-800">
                Profile Photo (optional)
              </div>
              <div className="mt-2">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  onChange={(e) => setProPic(e.target.files?.[0] || null)}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
                <div className="text-xs text-gray-500 mt-1">
                  Allowed: jpg, jpeg, png, webp. Max: 2MB.
                </div>
                {proPic ? (
                  <div className="text-xs text-gray-700 mt-2">
                    Selected: <span className="font-medium">{proPic.name}</span>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}

        {step === 2 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Gender">
              <select
                value={form.gender}
                onChange={(e) => set("gender", e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Select</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </Field>

            <Field label="Date of birth">
              <input
                type="date"
                value={form.dob}
                onChange={(e) => set("dob", e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </Field>

            <Field label="Marital status">
              <select
                value={form.marital_status}
                onChange={(e) => set("marital_status", e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Select</option>
                <option value="single">Single</option>
                <option value="married">Married</option>
                <option value="divorced">Divorced</option>
                <option value="widowed">Widowed</option>
              </select>
            </Field>

            <Field label="Nationality">
              <input
                value={form.nationality}
                onChange={(e) => set("nationality", e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </Field>

            <Field label="State of origin">
              <select
                value={stateOfOriginId ?? ""}
                onChange={(e) => {
                  const id = Number(e.target.value) || null;
                  const name = (states as any[]).find((s) => s.id === id)?.name ?? "";
                  setStateOfOriginId(id);
                  set("state_of_origin", name);
                  set("lga", "");
                }}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Select state</option>
                {(states as any[]).map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </Field>

            <Field label="LGA">
              <select
                value={form.lga}
                onChange={(e) => set("lga", e.target.value)}
                disabled={!stateOfOriginId}
                className="w-full border rounded-lg px-3 py-2 text-sm disabled:opacity-50"
              >
                <option value="">
                  {stateOfOriginId ? "Select LGA" : "Select state first"}
                </option>
                {(lgas as any[]).map((l) => (
                  <option key={l.id} value={l.name}>{l.name}</option>
                ))}
              </select>
            </Field>
          </div>
        ) : null}

        {step === 3 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Phone">
              <input
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </Field>

            <Field label="Phone 2">
              <input
                value={form.phone2}
                onChange={(e) => set("phone2", e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </Field>

            <Field label="Address line 1">
              <input
                value={form.address_line1}
                onChange={(e) => set("address_line1", e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </Field>

            <Field label="Address line 2">
              <input
                value={form.address_line2}
                onChange={(e) => set("address_line2", e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </Field>

            <Field label="City">
              <input
                value={form.city}
                onChange={(e) => set("city", e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </Field>

            <Field label="State">
              <select
                value={contactStateId ?? ""}
                onChange={(e) => {
                  const id = Number(e.target.value) || null;
                  const name = (states as any[]).find((s) => s.id === id)?.name ?? "";
                  setContactStateId(id);
                  set("state", name);
                  set("contact_lga", "");
                }}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Select state</option>
                {(states as any[]).map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </Field>

            <Field label="LGA">
              <select
                value={form.contact_lga}
                onChange={(e) => set("contact_lga", e.target.value)}
                disabled={!contactStateId}
                className="w-full border rounded-lg px-3 py-2 text-sm disabled:opacity-50"
              >
                <option value="">
                  {contactStateId ? "Select LGA" : "Select state first"}
                </option>
                {(contactLgas as any[]).map((l) => (
                  <option key={l.id} value={l.name}>{l.name}</option>
                ))}
              </select>
            </Field>

            <Field label="Postal code">
              <input
                value={form.postal_code}
                onChange={(e) => set("postal_code", e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </Field>

            <Field label="Country">
              <input
                value={form.country}
                onChange={(e) => set("country", e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </Field>

            <div className="md:col-span-2 border rounded-xl p-4">
              <div className="font-semibold text-gray-800">
                Emergency Contact
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                <Field label="Name">
                  <input
                    value={form.emergency_name}
                    onChange={(e) => set("emergency_name", e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </Field>
                <Field label="Relationship">
                  <select
                    value={form.emergency_relationship}
                    onChange={(e) => set("emergency_relationship", e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="">Select</option>
                    <option value="spouse">Spouse</option>
                    <option value="parent">Parent</option>
                    <option value="sibling">Sibling</option>
                    <option value="child">Child</option>
                    <option value="friend">Friend</option>
                    <option value="colleague">Colleague</option>
                    <option value="other">Other</option>
                  </select>
                </Field>
                <Field label="Phone">
                  <input
                    value={form.emergency_phone}
                    onChange={(e) => set("emergency_phone", e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </Field>
                <Field label="Address">
                  <input
                    value={form.emergency_address}
                    onChange={(e) => set("emergency_address", e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </Field>
              </div>
            </div>
          </div>
        ) : null}

        {step === 4 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Employment date">
              <input
                type="date"
                value={form.employment_date}
                onChange={(e) => set("employment_date", e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </Field>

            <Field label="Employment type">
              <select
                value={form.employment_type}
                onChange={(e) => set("employment_type", e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Select type</option>
                <option value="full-time">Full-time</option>
                <option value="part-time">Part-time</option>
                <option value="contract">Contract</option>
                <option value="intern">Intern</option>
                <option value="temporary">Temporary</option>
                <option value="consultant">Consultant</option>
              </select>
            </Field>

            <Field label="Job title">
              <input
                value={form.job_title}
                onChange={(e) => set("job_title", e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </Field>

            <div className="md:col-span-3 border rounded-xl p-4">
              <div className="font-semibold text-gray-800">Next of Kin</div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
                <Field label="Name">
                  <input
                    value={form.nok_name}
                    onChange={(e) => set("nok_name", e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </Field>
                <Field label="Relationship">
                  <select
                    value={form.nok_relationship}
                    onChange={(e) => set("nok_relationship", e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="">Select</option>
                    <option value="spouse">Spouse</option>
                    <option value="parent">Parent</option>
                    <option value="sibling">Sibling</option>
                    <option value="child">Child</option>
                    <option value="friend">Friend</option>
                    <option value="other">Other</option>
                  </select>
                </Field>
                <Field label="Phone">
                  <input
                    value={form.nok_phone}
                    onChange={(e) => set("nok_phone", e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </Field>
                <Field label="Email">
                  <input
                    value={form.nok_email}
                    onChange={(e) => set("nok_email", e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </Field>
              </div>
            </div>

            <div className="md:col-span-3 border rounded-xl p-4">
              <div className="font-semibold text-gray-800">
                Bank / Payroll (optional)
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
                <Field label="Bank name">
                  <select
                    value={form.bank_name}
                    onChange={(e) => set("bank_name", e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="">Select bank</option>
                    <option value="Access Bank">Access Bank</option>
                    <option value="Citibank Nigeria">Citibank Nigeria</option>
                    <option value="Ecobank Nigeria">Ecobank Nigeria</option>
                    <option value="Fidelity Bank">Fidelity Bank</option>
                    <option value="First Bank of Nigeria">First Bank of Nigeria</option>
                    <option value="First City Monument Bank (FCMB)">First City Monument Bank (FCMB)</option>
                    <option value="Globus Bank">Globus Bank</option>
                    <option value="Guaranty Trust Bank (GTBank)">Guaranty Trust Bank (GTBank)</option>
                    <option value="Heritage Bank">Heritage Bank</option>
                    <option value="Keystone Bank">Keystone Bank</option>
                    <option value="Kuda Bank">Kuda Bank</option>
                    <option value="Lotus Bank">Lotus Bank</option>
                    <option value="Moniepoint MFB">Moniepoint MFB</option>
                    <option value="OPay Digital Services">OPay Digital Services</option>
                    <option value="Opay">Opay</option>
                    <option value="Palmpay">Palmpay</option>
                    <option value="Parallex Bank">Parallex Bank</option>
                    <option value="Polaris Bank">Polaris Bank</option>
                    <option value="Premium Trust Bank">Premium Trust Bank</option>
                    <option value="Providus Bank">Providus Bank</option>
                    <option value="Stanbic IBTC Bank">Stanbic IBTC Bank</option>
                    <option value="Standard Chartered Bank">Standard Chartered Bank</option>
                    <option value="Sterling Bank">Sterling Bank</option>
                    <option value="SunTrust Bank">SunTrust Bank</option>
                    <option value="Titan Trust Bank">Titan Trust Bank</option>
                    <option value="Union Bank of Nigeria">Union Bank of Nigeria</option>
                    <option value="United Bank for Africa (UBA)">United Bank for Africa (UBA)</option>
                    <option value="Unity Bank">Unity Bank</option>
                    <option value="VFD Microfinance Bank">VFD Microfinance Bank</option>
                    <option value="Wema Bank">Wema Bank</option>
                    <option value="Zenith Bank">Zenith Bank</option>
                  </select>
                </Field>
                <Field label="Account name">
                  <input
                    value={form.bank_account_name}
                    onChange={(e) => set("bank_account_name", e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </Field>
                <Field label="Account number">
                  <input
                    value={form.bank_account_number}
                    onChange={(e) => set("bank_account_number", e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </Field>
                <Field label="Sort code">
                  <input
                    value={form.bank_sort_code}
                    onChange={(e) => set("bank_sort_code", e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </Field>
              </div>

              <div className="mt-4">
                <label className="text-sm text-gray-600">Notes</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => set("notes", e.target.value)}
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                  rows={3}
                />
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <div className="mt-6 flex items-center justify-between">
        <button
          type="button"
          onClick={back}
          disabled={step === 1 || isLoading}
          className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm disabled:opacity-60"
        >
          Back
        </button>

        <div className="flex gap-2">
          {step < 4 ? (
            <button
              type="button"
              onClick={next}
              disabled={isLoading}
              className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 text-sm disabled:opacity-60"
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              onClick={onSubmit}
              disabled={isLoading}
              className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 text-sm disabled:opacity-60 inline-flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                  Creating…
                </>
              ) : (
                "Create Admin"
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: any }) {
  return (
    <div>
      <label className="text-sm text-gray-600">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
